import React, { useEffect, useRef, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

const WIDGET_SCRIPT_ID = 'sc-zoey-widget-script';
const WIDGET_STYLE_ID = 'sc-zoey-widget-style';
const WIDGET_ATTENTION_STYLE_ID = 'sc-zoey-widget-attention-style';
const WIDGET_ASSET_BASE_URL = '/zoey-widget';
const WIDGET_SCRIPT_URL = `${WIDGET_ASSET_BASE_URL}/zoey-widget.iife.js`;
const WIDGET_STYLE_CANDIDATES = [
  `${WIDGET_ASSET_BASE_URL}/style.css`,
  `${WIDGET_ASSET_BASE_URL}/zoey-widget.css`,
];
const WIDGET_CONTAINER_ID = 'zoey-widget-container';
const WIDGET_ROOT_ID = 'jeonsoft-zoey-widget-root';
const WIDGET_LAUNCHER_SELECTOR = '.zw-orbButton';
const WIDGET_LAUNCHER_TOOLTIP = 'Drag and drop to change position, then click to open Zoey.';
const ATTENTION_ACTIVE_VALUE = 'active';
const ATTENTION_INACTIVE_VALUE = 'inactive';
const TOOLTIP_VISIBLE_VALUE = 'visible';
const TOOLTIP_HIDDEN_VALUE = 'hidden';
const LAUNCHER_POSITION_STORAGE_KEY = 'sc-zoey-launcher-position';
const LAUNCHER_VIEWPORT_PADDING = 24;
const LAUNCHER_DRAG_THRESHOLD = 6;

// Keeps the launcher position inside the visible viewport.
function clampLauncherPosition(position, size) {
  const launcherWidth = Math.max(size?.width || 0, 72);
  const launcherHeight = Math.max(size?.height || 0, 72);
  const maxX = Math.max(LAUNCHER_VIEWPORT_PADDING, window.innerWidth - launcherWidth - LAUNCHER_VIEWPORT_PADDING);
  const maxY = Math.max(LAUNCHER_VIEWPORT_PADDING, window.innerHeight - launcherHeight - LAUNCHER_VIEWPORT_PADDING);

  return {
    x: Math.min(Math.max(position.x, LAUNCHER_VIEWPORT_PADDING), maxX),
    y: Math.min(Math.max(position.y, LAUNCHER_VIEWPORT_PADDING), maxY),
  };
}

// Reads the last user-selected launcher position from local storage.
function readSavedLauncherPosition() {
  try {
    const raw = window.localStorage.getItem(LAUNCHER_POSITION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!Number.isFinite(parsed?.x) || !Number.isFinite(parsed?.y)) {
      return null;
    }

    return {
      x: Number(parsed.x),
      y: Number(parsed.y),
    };
  }
  catch (_err) {
    return null;
  }
}

// Persists the launcher position so users do not need to drag it again after refresh.
function saveLauncherPosition(position) {
  try {
    window.localStorage.setItem(LAUNCHER_POSITION_STORAGE_KEY, JSON.stringify(position));
  }
  catch (_err) {
    // Ignore storage failures so the widget remains usable.
  }
}

// Applies the current Support Central launcher position override to the widget root.
function applyLauncherPosition(root, position) {
  if (!root || !position) {
    return;
  }

  root.style.left = `${position.x}px`;
  root.style.top = `${position.y}px`;
  root.style.right = 'auto';
  root.style.bottom = 'auto';
}

// Restores the shared widget's default bottom-right anchoring when the chat panel is open.
function resetWidgetRootPosition(root) {
  if (!root) {
    return;
  }

  root.style.left = '';
  root.style.top = '';
  root.style.right = '';
  root.style.bottom = '';
}

// Keeps the launcher tooltip readable when the launcher is near the top edge.
function updateLauncherTooltipPlacement(root) {
  if (!root) {
    return;
  }

  root.setAttribute('data-sc-launcher-tooltip-placement', root.getBoundingClientRect().top < 96 ? 'bottom' : 'top');
}

// Controls the SC-owned launcher tooltip without relying on root hover state.
function setLauncherTooltipState(root, isVisible) {
  if (!root) {
    return;
  }

  root.setAttribute('data-sc-launcher-tooltip', isVisible ? TOOLTIP_VISIBLE_VALUE : TOOLTIP_HIDDEN_VALUE);
}

// Loads one widget stylesheet only once for the current page.
function loadStyle(href, id) {
  const existing = document.getElementById(id);
  if (existing) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve(href);
    link.onerror = () => {
      link.remove();
      reject(new Error(`Unable to load ${href}`));
    };
    document.head.appendChild(link);
  });
}

// Tries the SC-owned widget stylesheet candidates until one succeeds.
async function loadFirstAvailableStyle(candidates, id) {
  let lastError = null;
  for (const href of candidates) {
    try {
      return await loadStyle(href, id);
    }
    catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error('Unable to load Zoey widget styles.');
}

// Loads the widget bundle only once and waits for the global entrypoint.
function loadScript(src, id) {
  const existing = document.getElementById(id);
  if (existing) {
    if (window.ZoeyWidget) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Unable to load ${src}`)), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Unable to load ${src}`));
    document.body.appendChild(script);
  });
}

// Injects the Support Central-only launcher attention styling on top of the shared orb button skin.
function ensureAttentionStyle() {
  if (document.getElementById(WIDGET_ATTENTION_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = WIDGET_ATTENTION_STYLE_ID;
  style.textContent = `
    #${WIDGET_ROOT_ID}[data-sc-launcher-attention='${ATTENTION_ACTIVE_VALUE}'] ${WIDGET_LAUNCHER_SELECTOR} {
      animation: sc-zoey-launcher-nudge 6s ease-in-out infinite;
      transform-origin: center;
      cursor: grab;
    }

    #${WIDGET_ROOT_ID}[data-sc-launcher-attention='${ATTENTION_ACTIVE_VALUE}'] ${WIDGET_LAUNCHER_SELECTOR}::after {
      animation:
        zw-orb-orbit 1.35s linear infinite,
        zw-orb-hue 3.2s linear infinite,
        sc-zoey-launcher-beacon 6s ease-out infinite;
    }

    #${WIDGET_ROOT_ID}[data-sc-launcher-attention='${ATTENTION_ACTIVE_VALUE}'] ${WIDGET_LAUNCHER_SELECTOR}::before {
      animation:
        zw-orb-orbit 1.35s linear infinite,
        zw-orb-hue 3.2s linear infinite,
        sc-zoey-launcher-halo 6s ease-out infinite;
    }

    @keyframes sc-zoey-launcher-nudge {
      0%, 76%, 100% {
        transform: translate3d(0, 0, 0) scale(1);
      }
      80% {
        transform: translate3d(0, -2px, 0) scale(1.03);
      }
      84% {
        transform: translate3d(0, 1px, 0) scale(1.08);
      }
      88% {
        transform: translate3d(0, -3px, 0) scale(1.04);
      }
      92% {
        transform: translate3d(0, 0, 0) scale(1);
      }
    }

    @keyframes sc-zoey-launcher-beacon {
      0%, 76%, 100% {
        opacity: 0.75;
        box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
      }
      84% {
        opacity: 1;
        box-shadow: 0 0 0 14px rgba(59, 130, 246, 0.12);
      }
      92% {
        opacity: 0.8;
        box-shadow: 0 0 0 28px rgba(34, 211, 238, 0);
      }
    }

    @keyframes sc-zoey-launcher-halo {
      0%, 76%, 100% {
        opacity: 1;
      }
      84% {
        opacity: 1.2;
        filter: blur(22px) saturate(1.9) brightness(1.55) hue-rotate(85deg);
      }
      92% {
        opacity: 0.9;
      }
    }

    #${WIDGET_ROOT_ID}[data-sc-launcher-draggable='true'] ${WIDGET_LAUNCHER_SELECTOR} {
      cursor: grab;
      touch-action: none;
      user-select: none;
      -webkit-user-select: none;
    }

    #${WIDGET_ROOT_ID}[data-sc-launcher-dragging='true'] ${WIDGET_LAUNCHER_SELECTOR} {
      cursor: grabbing;
    }

    #${WIDGET_ROOT_ID}[data-sc-launcher-draggable='true']::after {
      content: attr(data-sc-launcher-tooltip-text);
      position: absolute;
      right: 0;
      bottom: calc(100% + 10px);
      max-width: 260px;
      padding: 8px 10px;
      border-radius: 6px;
      background: rgba(15, 23, 42, 0.94);
      color: #fff;
      font-size: 12px;
      line-height: 1.35;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.22);
      opacity: 0;
      pointer-events: none;
      transform: translateY(2px);
      transition: opacity 80ms ease, transform 80ms ease;
      white-space: normal;
      width: max-content;
      z-index: 10000;
    }

    #${WIDGET_ROOT_ID}[data-sc-launcher-tooltip-placement='bottom']::after {
      top: calc(100% + 10px);
      bottom: auto;
    }

    #${WIDGET_ROOT_ID}[data-sc-launcher-tooltip='${TOOLTIP_VISIBLE_VALUE}']::after {
      opacity: 1;
      transform: translateY(0);
    }

    #${WIDGET_ROOT_ID}[data-sc-launcher-dragging='true']::after,
    #${WIDGET_ROOT_ID}[data-sc-launcher-tooltip='${TOOLTIP_HIDDEN_VALUE}']::after {
      opacity: 0;
    }

    @media (prefers-reduced-motion: reduce) {
      #${WIDGET_ROOT_ID}[data-sc-launcher-attention='${ATTENTION_ACTIVE_VALUE}'] ${WIDGET_LAUNCHER_SELECTOR},
      #${WIDGET_ROOT_ID}[data-sc-launcher-attention='${ATTENTION_ACTIVE_VALUE}'] ${WIDGET_LAUNCHER_SELECTOR}::before,
      #${WIDGET_ROOT_ID}[data-sc-launcher-attention='${ATTENTION_ACTIVE_VALUE}'] ${WIDGET_LAUNCHER_SELECTOR}::after {
        animation: none !important;
      }
    }
  `;

  document.head.appendChild(style);
}

// Finds the widget root so Support Central can apply its local enhancement layer.
function getWidgetRoot() {
  return document.getElementById(WIDGET_ROOT_ID);
}

// Marks the page-session launcher state without persisting it across refreshes.
function setLauncherAttentionState(root, isActive) {
  if (!root) {
    return;
  }

  const nextValue = isActive ? ATTENTION_ACTIVE_VALUE : ATTENTION_INACTIVE_VALUE;
  if (root.getAttribute('data-sc-launcher-attention') === nextValue) {
    return;
  }

  root.setAttribute('data-sc-launcher-attention', nextValue);
}

// Removes the externally mounted widget container so logout does not leave the launcher behind.
function teardownWidgetContainer() {
  const widgetContainer = document.getElementById(WIDGET_CONTAINER_ID);
  if (widgetContainer) {
    widgetContainer.remove();
  }
}

// Loads the shared Zoey widget and layers Support Central-specific behavior on top of it.
const Zoey = () => {
  const initializedRef = useRef(false);
  const openedOnceRef = useRef(false);
  const sawClosedLauncherRef = useRef(false);
  const observerRef = useRef(null);
  const launcherPositionRef = useRef(null);
  const [widgetLoadError, setWidgetLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;

    // Reapplies the saved launcher position after viewport changes.
    function syncLauncherPositionWithinViewport() {
      const root = getWidgetRoot();
      if (!root) {
        return;
      }

      const savedPosition = launcherPositionRef.current;
      if (!savedPosition) {
        return;
      }

      const rect = root.getBoundingClientRect();
      const clampedPosition = clampLauncherPosition(savedPosition, {
        width: rect.width,
        height: rect.height,
      });
      launcherPositionRef.current = clampedPosition;
      applyLauncherPosition(root, clampedPosition);
      updateLauncherTooltipPlacement(root);
      saveLauncherPosition(clampedPosition);
    }

    // Enables dragging for the closed launcher and preserves normal click-to-open behavior.
    function bindLauncherDrag(root, launcherButton) {
      if (!root || !launcherButton || launcherButton.dataset.scDragBound === 'true') {
        return;
      }

      launcherButton.dataset.scDragBound = 'true';
      launcherButton.setAttribute('aria-label', WIDGET_LAUNCHER_TOOLTIP);
      root.setAttribute('data-sc-launcher-tooltip-text', WIDGET_LAUNCHER_TOOLTIP);
      root.setAttribute('data-sc-launcher-draggable', 'true');
      setLauncherTooltipState(root, false);
      updateLauncherTooltipPlacement(root);

      launcherButton.addEventListener('pointerenter', () => {
        if (root.getAttribute('data-sc-launcher-dragging') !== 'true') {
          setLauncherTooltipState(root, true);
        }
      });

      launcherButton.addEventListener('pointerleave', () => {
        setLauncherTooltipState(root, false);
      });

      launcherButton.addEventListener('focus', () => {
        if (root.getAttribute('data-sc-launcher-dragging') !== 'true') {
          setLauncherTooltipState(root, true);
        }
      });

      launcherButton.addEventListener('blur', () => {
        setLauncherTooltipState(root, false);
      });

      launcherButton.addEventListener('click', (event) => {
        if (launcherButton.dataset.scSuppressClick === 'true') {
          event.preventDefault();
          event.stopPropagation();
          launcherButton.dataset.scSuppressClick = 'false';
        }
      }, true);

      launcherButton.addEventListener('pointerdown', (event) => {
        if (event.button !== undefined && event.button !== 0) {
          return;
        }

        const rootRect = root.getBoundingClientRect();
        const dragState = {
          pointerId: event.pointerId,
          startClientX: event.clientX,
          startClientY: event.clientY,
          startX: rootRect.left,
          startY: rootRect.top,
          width: rootRect.width,
          height: rootRect.height,
          didDrag: false,
        };

        const handlePointerMove = (moveEvent) => {
          if (moveEvent.pointerId !== dragState.pointerId) {
            return;
          }

          const deltaX = moveEvent.clientX - dragState.startClientX;
          const deltaY = moveEvent.clientY - dragState.startClientY;
          if (!dragState.didDrag && Math.hypot(deltaX, deltaY) < LAUNCHER_DRAG_THRESHOLD) {
            return;
          }

          dragState.didDrag = true;
          setLauncherTooltipState(root, false);
          root.setAttribute('data-sc-launcher-dragging', 'true');
          const nextPosition = clampLauncherPosition({
            x: dragState.startX + deltaX,
            y: dragState.startY + deltaY,
          }, {
            width: dragState.width,
            height: dragState.height,
          });

          launcherPositionRef.current = nextPosition;
          applyLauncherPosition(root, nextPosition);
          updateLauncherTooltipPlacement(root);
          moveEvent.preventDefault();
        };

        const handlePointerUp = (upEvent) => {
          if (upEvent.pointerId !== dragState.pointerId) {
            return;
          }

          window.removeEventListener('pointermove', handlePointerMove);
          window.removeEventListener('pointerup', handlePointerUp);
          window.removeEventListener('pointercancel', handlePointerUp);
          root.setAttribute('data-sc-launcher-dragging', 'false');
          setLauncherTooltipState(root, false);

          if (dragState.didDrag && launcherPositionRef.current) {
            launcherButton.dataset.scSuppressClick = 'true';
            launcherButton.blur();
            saveLauncherPosition(launcherPositionRef.current);
          }
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);
      });
    }

    // Tracks the rendered widget DOM so the stronger launcher nudge only runs while closed.
    function syncLauncherAttention() {
      const root = getWidgetRoot();
      if (!root) {
        return;
      }

      const launcherButton = root.querySelector(WIDGET_LAUNCHER_SELECTOR);
      const launcherVisible = Boolean(launcherButton);

      if (launcherVisible) {
        sawClosedLauncherRef.current = true;
        syncLauncherPositionWithinViewport();
      }

      if (!launcherVisible && sawClosedLauncherRef.current === true) {
        openedOnceRef.current = true;
        setLauncherTooltipState(root, false);
        resetWidgetRootPosition(root);
      }

      setLauncherAttentionState(root, launcherVisible && openedOnceRef.current !== true);

      if (launcherButton) {
        bindLauncherDrag(root, launcherButton);
      }

      if (launcherButton && launcherButton.dataset.scAttentionBound !== 'true') {
        launcherButton.dataset.scAttentionBound = 'true';
        launcherButton.addEventListener('click', () => {
          openedOnceRef.current = true;
          setLauncherAttentionState(root, false);
        }, { once: true });
      }
    }

    // Observes widget DOM changes because the launcher disappears when the panel opens.
    function startLauncherAttentionObserver() {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      const container = document.getElementById(WIDGET_CONTAINER_ID) || document.body;
      const observer = new MutationObserver(() => {
        syncLauncherAttention();
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
      });

      observerRef.current = observer;
      syncLauncherAttention();
    }

    async function initWidget() {
      try {
        await loadFirstAvailableStyle(WIDGET_STYLE_CANDIDATES, WIDGET_STYLE_ID);
        await loadScript(WIDGET_SCRIPT_URL, WIDGET_SCRIPT_ID);
        ensureAttentionStyle();

        if (cancelled || initializedRef.current === true) {
          return;
        }

        if (!window.ZoeyWidget?.init) {
          throw new Error('Zoey widget bundle loaded without the ZoeyWidget.init entrypoint.');
        }

        initializedRef.current = true;
        window.ZoeyWidget.init({
          chatEndPoint: '/api/zoey/chat',
          conversationEndPoint: '/api/zoey/conversations',
          chatsEndPoint: '/api/zoey/chats',
          assetBaseUrl: WIDGET_ASSET_BASE_URL,
          allowTicketCreation: false,
        });
        launcherPositionRef.current = readSavedLauncherPosition();
        syncLauncherPositionWithinViewport();
        startLauncherAttentionObserver();
      }
      catch (err) {
        if (!cancelled) {
          setWidgetLoadError(err.message || 'Zoey widget assets are unavailable in this environment.');
        }
      }
    }

    initWidget();
    window.addEventListener('resize', syncLauncherPositionWithinViewport);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', syncLauncherPositionWithinViewport);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      initializedRef.current = false;
      openedOnceRef.current = false;
      sawClosedLauncherRef.current = false;
      launcherPositionRef.current = null;
      teardownWidgetContainer();
    };
  }, []);

  return (
    <Snackbar
      open={Boolean(widgetLoadError)}
      autoHideDuration={6000}
      onClose={() => setWidgetLoadError('')}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="error" variant="filled" onClose={() => setWidgetLoadError('')}>
        {widgetLoadError}
      </Alert>
    </Snackbar>
  );
};

export default Zoey;
